import {
  DAY_LABELS,
  heatmapIntensity,
} from '../../../../shared/lib/analytics-format'
import type { HeatmapCell } from '../../../../shared/types/analytics'
import { useTranslation } from 'react-i18next'

interface HourlyHeatmapProps {
  cells: HeatmapCell[]
  timeZone: string
}

const HOURS = Array.from({ length: 24 }, (_, hour) => hour)
const HOUR_TICKS = [0, 6, 12, 18]

/** 요일 × 시간 클릭 밀도 — 언제 트래픽이 몰리는지 한눈에 */
export default function HourlyHeatmap({ cells, timeZone }: HourlyHeatmapProps) {
  const { t } = useTranslation()
  const max = cells.reduce((peak, cell) => Math.max(peak, cell.clicks), 0)
  const byKey = new Map(
    cells.map(cell => [`${cell.dayOfWeek}:${cell.hour}`, cell.clicks])
  )

  return (
    <section className="rounded-2xl border border-gray-200 bg-white p-5 dark:border-gray-700 dark:bg-gray-800">
      <div className="mb-4 flex items-baseline justify-between gap-4">
        <h3 className="text-sm font-semibold uppercase tracking-widest text-gray-500 dark:text-gray-400">
          {t('analyticsPage.hourlyTitle')}
        </h3>
        <span className="text-xs text-gray-500 dark:text-gray-400">
          {t('analyticsPage.timezoneNote', { tz: timeZone })}
        </span>
      </div>

      <div className="overflow-x-auto">
        <div className="min-w-[520px]">
          {DAY_LABELS.map((day, dayIndex) => (
            <div key={day} className="flex items-center gap-2">
              <span className="w-5 shrink-0 text-xs text-gray-500 dark:text-gray-400">
                {day}
              </span>
              <div className="flex flex-1 gap-[3px] py-[2px]">
                {HOURS.map(hour => {
                  const clicks = byKey.get(`${dayIndex}:${hour}`) ?? 0
                  const intensity = heatmapIntensity(clicks, max)
                  return (
                    <div
                      key={hour}
                      title={`${day}요일 ${hour}시 · ${clicks.toLocaleString()}회`}
                      className="h-5 flex-1 rounded-[3px] bg-primary-500 transition-transform hover:scale-110"
                      style={{
                        opacity: intensity === 0 ? 0.06 : intensity,
                      }}
                    />
                  )
                })}
              </div>
            </div>
          ))}

          <div className="mt-2 flex items-center gap-2">
            <span className="w-5 shrink-0" />
            <div className="flex flex-1 gap-[3px]">
              {HOURS.map(hour => (
                <span
                  key={hour}
                  className="flex-1 text-center text-[10px] text-gray-400"
                >
                  {HOUR_TICKS.includes(hour) ? hour : ''}
                </span>
              ))}
            </div>
          </div>
        </div>
      </div>

      {max === 0 && (
        <p className="mt-4 text-sm text-gray-500 dark:text-gray-400">
          {t('analyticsPage.noClicks')}
        </p>
      )}
    </section>
  )
}
